import { useState, useEffect } from 'react';
import { ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FONDO_LABELS, NATIVE_FIELD_CLASS } from '@/contabilidad/constants/ui';
import { formatAmount } from '@/contabilidad/utils/formatters';
import { NumberInput } from '@/components/ui/NumberInput';
import type { Moneda, CuentaBancaria, CreateMovimientoPayload, FondoContable, Movimiento } from '@/contabilidad/types';
import { cn } from '@/lib/utils';
import { FormField } from './FormField';
import { NativeSelect } from './NativeSelect';
import { DateInput } from './DateInput';
import { CategoriaAutocomplete } from './CategoriaAutocomplete';
import { BeneficiarioAutocomplete } from './BeneficiarioAutocomplete';

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

interface Props {
  open: boolean;
  tipo: 'Ingreso' | 'Egreso';
  cajas: CuentaBancaria[];
  /** Si se pasa, el modal arranca en modo edición con los valores precargados. */
  movimiento?: Movimiento;
  isSubmitting?: boolean;
  onSubmit: (payload: CreateMovimientoPayload) => Promise<void>;
  onClose: () => void;
}

interface FormState {
  categoria: string;
  monto: string;
  moneda: Moneda;
  cajaId: string;
  fecha: string;
  pagador: string;
  descripcion: string;
  fondo: FondoContable;
}

const EMPTY: FormState = {
  categoria: '',
  monto: '',
  moneda: 'ARS',
  cajaId: '',
  fecha: todayISO(),
  pagador: '',
  descripcion: '',
  fondo: 'escrituras',
};

/** Convierte fecha DD/MM/YYYY → YYYY-MM-DD (para pre-poblar el input[type=date]). */
function localToISO(ddmmyyyy: string): string {
  const [d, m, y] = ddmmyyyy.split('/');
  if (!d || !m || !y) return todayISO();
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

export function RegistrarMovimientoModal({ open, tipo, cajas, movimiento, isSubmitting = false, onSubmit, onClose }: Props) {
  const isEditing = !!movimiento;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, boolean>>>({});

  useEffect(() => {
    if (!open) return;
    if (movimiento) {
      // Modo edición: precargar valores del movimiento
      const cajaMatch = cajas.find((c) => c.nombre === movimiento.caja);
      setForm({
        categoria: movimiento.categoria,
        monto: String(Math.abs(movimiento.monto)),
        moneda: movimiento.moneda,
        cajaId: cajaMatch ? String(cajaMatch.id) : '',
        fecha: localToISO(movimiento.fecha),
        pagador: movimiento.pagador ?? '',
        descripcion: movimiento.descripcion,
        fondo: movimiento.fondo ?? 'escrituras',
      });
    } else {
      setForm({ ...EMPTY, fecha: todayISO() });
    }
    setErrors({});
  }, [open, movimiento, cajas]);

  const isIngreso = tipo === 'Ingreso';
  // El fondo de un movimiento generado (cobro, pago, reparto) lo define su origen.
  const permiteElegirFondo = !movimiento?.sourceItemType;
  const Icon = isIngreso ? ArrowUpCircle : ArrowDownCircle;
  const cajaLabel = isIngreso ? 'Caja Destino' : 'Caja Origen';
  const pagadorLabel = isIngreso ? 'Pagador / Cliente' : 'Beneficiario / Proveedor';
  const submitLabel = isEditing
    ? 'Guardar Cambios'
    : isIngreso ? 'Registrar Ingreso' : 'Registrar Egreso';
  const modalTitle = isEditing
    ? `Editar ${tipo}`
    : isIngreso ? 'Registrar Ingreso' : 'Registrar Egreso';

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: false }));
  }

  async function handleSubmit() {
    const newErrors: typeof errors = {
      categoria: !form.categoria,
      monto: !form.monto || Number(form.monto) <= 0,
      cajaId: !form.cajaId,
      fecha: !form.fecha,
    };
    if (Object.values(newErrors).some(Boolean)) {
      setErrors(newErrors);
      return;
    }

    await onSubmit({
      tipo,
      categoria: form.categoria,
      monto: Number(form.monto),
      moneda: form.moneda,
      cajaId: Number(form.cajaId),
      fecha: form.fecha,
      pagador: form.pagador || undefined,
      descripcion: form.descripcion || undefined,
      fondo: permiteElegirFondo ? form.fondo : undefined,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <Icon className={cn('size-5', isIngreso ? 'text-blue-600' : 'text-slate-600')} />
            {modalTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">

          <FormField label="Categoría" required>
            <CategoriaAutocomplete
              tipo={tipo}
              value={form.categoria}
              onChange={(value) => set('categoria', value)}
              error={errors.categoria}
            />
          </FormField>

          <div className="flex gap-3">
            <FormField label="Monto" required className="flex-1">
              <NumberInput
                id="monto"
                decimals={2}
                min={0}
                placeholder="0"
                value={form.monto}
                onChange={(value) => set('monto', value)}
                className={cn(NATIVE_FIELD_CLASS, errors.monto && 'border-rose-500 focus:border-rose-500', 'text-right')}
              />
            </FormField>
            <FormField label="Moneda" required className="flex-1">
              <NativeSelect
                value={form.moneda}
                onChange={(e) => set('moneda', e.target.value as Moneda)}
              >
                <option value="ARS">ARS - Pesos</option>
                <option value="USD">USD - Dólares</option>
              </NativeSelect>
            </FormField>
          </div>

          <FormField label={cajaLabel} required>
            <NativeSelect
              value={form.cajaId}
              onChange={(e) => set('cajaId', e.target.value)}
              error={errors.cajaId}
            >
              <option value="">Seleccionar caja...</option>
              {cajas.map((caja) => (
                <option key={caja.id} value={String(caja.id)}>
                  {caja.nombre} (Saldo: {caja.moneda} $ {formatAmount(caja.saldo, caja.moneda)})
                </option>
              ))}
            </NativeSelect>
          </FormField>

          {permiteElegirFondo && (
            <FormField label="Fondo" required>
              <NativeSelect
                aria-label="Fondo"
                value={form.fondo}
                onChange={(e) => set('fondo', e.target.value as FondoContable)}
              >
                {(Object.keys(FONDO_LABELS) as FondoContable[]).map((fondo) => (
                  <option key={fondo} value={fondo}>{FONDO_LABELS[fondo]}</option>
                ))}
              </NativeSelect>
            </FormField>
          )}

          <div className="flex gap-3">
            <FormField label="Fecha" required className="flex-1">
              <DateInput
                value={form.fecha}
                onChange={(e) => set('fecha', e.target.value)}
                error={errors.fecha}
              />
            </FormField>
            <FormField label={pagadorLabel} className="flex-1">
              <BeneficiarioAutocomplete
                value={form.pagador}
                onChange={(value) => set('pagador', value)}
                placeholder="Opcional"
              />
            </FormField>
          </div>

          <FormField label="Descripción">
            <textarea
              placeholder="Descripción opcional..."
              value={form.descripcion}
              onChange={(e) => set('descripcion', e.target.value)}
              className={cn(NATIVE_FIELD_CLASS, 'h-auto min-h-[80px] py-2 resize-none')}
            />
          </FormField>

        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="rounded-full">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} className="rounded-full">
            {isSubmitting ? 'Guardando...' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
